/**
 * Icon Context Provider
 *
 * Provides global icon configuration using React Context API.
 * Follows the same pattern as ThemeProvider and SizingProvider.
 *
 * The IconProvider should be placed in the provider hierarchy after
 * ThemeProvider and SizingProvider since icons may depend on theme colors
 * and sizing values.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <ThemeProvider>
 *   <SizingProvider>
 *     <IconProvider>
 *       <YourApp />
 *     </IconProvider>
 *   </SizingProvider>
 * </ThemeProvider>
 * ```
 */

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { IconContextValue, IconSize } from './types';
import { logger } from '@/utils/logger';

/**
 * Context for icon configuration.
 */
const IconContext = createContext<IconContextValue | undefined>(undefined);

/**
 * Props for the IconProvider component.
 */
interface IconProviderProps {
  children: ReactNode;
  /**
   * Default size for icons when not specified.
   * @default 'md'
   */
  defaultSize?: IconSize;
}

/**
 * IconProvider component that manages global icon configuration.
 *
 * Provides:
 * - Default icon size configuration
 * - Future: Icon size preference persistence
 *
 * @example
 * ```tsx
 * <IconProvider defaultSize="md">
 *   <App />
 * </IconProvider>
 * ```
 */
export function IconProvider({
  children,
  defaultSize = 'md',
}: IconProviderProps) {
  const [currentDefaultSize] = useState<IconSize>(defaultSize);

  // Future enhancement: Load/save icon size preferences from localStorage
  useEffect(() => {
    // Could implement user preference loading here
    // Example:
    // const savedSize = StorageService.get<IconSize>(StorageKey.ICON_DEFAULT_SIZE);
    // if (savedSize) setCurrentDefaultSize(savedSize);
  }, []);

  const value: IconContextValue = {
    defaultSize: currentDefaultSize,
  };

  return <IconContext.Provider value={value}>{children}</IconContext.Provider>;
}

/**
 * Hook to access the icon context.
 *
 * Must be used within an IconProvider.
 *
 * @throws Error if used outside of IconProvider
 * @returns Icon context value
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { defaultSize } = useIconContext();
 *   return <div>Default icon size: {defaultSize}</div>;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export function useIconContext(): IconContextValue {
  const context = useContext(IconContext);

  if (!context) {
    // Provide degraded functionality with warning
    logger.ui.warn(
      'useIconContext must be used within an IconProvider. ' +
        'Using fallback default size "md". ' +
        'Wrap your app in <IconProvider> to remove this warning.'
    );
    return { defaultSize: 'md' };
  }

  return context;
}
